import React, { useState } from 'react';
import { Box, Chip, TextField, Typography } from '@mui/material';
import {
  Close as CloseIcon,
  KeyboardReturn as KeyboardReturnIcon,
} from '@mui/icons-material';

/**
 * 태그 입력 컴포넌트
 * @param {Array} value - 태그 배열
 * @param {Function} onChange - 변경 콜백
 * @param {String} placeholder - 플레이스홀더
 * @param {Boolean} disabled - 비활성화 여부
 * @param {Object} sx - 스타일 오버라이드
 */
function TagInput({
  value = [],
  onChange,
  placeholder = 'Press enter to add tags',
  disabled = false,
  sx = {},
}) {
  const [inputValue, setInputValue] = useState('');

  const handleAddTag = () => {
    if (!inputValue.trim()) return;

    const trimmedValue = inputValue.trim();
    if (value.includes(trimmedValue)) {
      setInputValue('');
      return;
    }

    const newTags = [...value, trimmedValue];
    onChange?.(newTags);
    setInputValue('');
  };

  const handleDeleteTag = (tagToDelete) => {
    const newTags = value.filter((tag) => tag !== tagToDelete);
    onChange?.(newTags);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  return (
    <Box
      sx={{
        border: '1px solid #81c784',
        borderRadius: '8px',
        padding: '12px',
        backgroundColor: '#ffffff',
        ...sx,
      }}
    >
      <TextField
        fullWidth
        size="small"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyPress={handleKeyPress}
        placeholder={placeholder}
        disabled={disabled}
        sx={{
          mb: 0.5,
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#ffffff',
            '& fieldset': {
              borderColor: 'transparent',
            },
            '&:hover fieldset': {
              borderColor: 'transparent',
            },
            '&.Mui-focused fieldset': {
              borderColor: 'transparent',
            },
          },
        }}
      />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          mb: value.length > 0 ? 1 : 0,
          px: 0.5,
        }}
      >
        <KeyboardReturnIcon
          sx={{
            fontSize: '14px',
            color: '#81c784',
          }}
        />
        <Typography
          variant="caption"
          sx={{
            color: '#81c784',
            fontSize: '0.75rem',
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Box
            component="span"
            sx={{
              backgroundColor: '#e8f5e9',
              padding: '2px 6px',
              borderRadius: '4px',
              mr: 0.5,
              fontWeight: 600,
              color: '#2e7d32',
            }}
          >
            Enter
          </Box>
          키를 눌러 태그를 추가하세요
        </Typography>
      </Box>
      {value.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            minHeight: '32px',
            padding: '8px 4px',
          }}
        >
          {value.map((tag, index) => (
            <Chip
              key={index}
              label={tag}
              onDelete={disabled ? undefined : () => handleDeleteTag(tag)}
              deleteIcon={
                <CloseIcon
                  sx={{
                    fontSize: '16px',
                    color: '#4caf50',
                    '&:hover': {
                      color: '#388e3c',
                    },
                  }}
                />
              }
              sx={{
                backgroundColor: '#eeeeee',
                color: '#000000',
                fontWeight: 400,
                fontSize: '0.875rem',
                height: '28px',
                borderRadius: '16px',
                '& .MuiChip-label': {
                  padding: '0 8px',
                },
                '& .MuiChip-deleteIcon': {
                  margin: '0 4px 0 -4px',
                  fontSize: '16px',
                },
                '&:hover': {
                  backgroundColor: '#e0e0e0',
                },
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

export default TagInput;
